import { useMemo } from "react";

import {
  Badge,
  FontIcon,
  Table,
  TableColumnDefinition,
  TableQuery,
  TableRowDefinition,
} from "@ezmid/components-library";
import {
  faCheckCircle,
  faEnvelope,
  faFingerprint,
  faList,
  faStopCircle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useGetBackOfficeListUsersQuery } from "../../../api/users.generated";
import { useFormatters } from "../../../hooks";
import { makeRoute } from "../../../hooks/route";
import { useTableQuery } from "../../../hooks/useTableQuery";
import { AllRoutes } from "../../route/Route";

const localStorageKey = "user.page";

type TableData = {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  status: string;
};

export function UsersListPageContent<T extends object>() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatDate } = useFormatters();
  const { query, updateQuery } = useTableQuery(
    localStorageKey,
    {
      pageSize: 10,
      pageIndex: 1,
      keyword: "",
      sortBy: { key: "name", order: "desc" },
      columnCategoryFilter: [],
    },
    AllRoutes.USERS_LIST
  );

  const { currentData, isFetching, isError } = useGetBackOfficeListUsersQuery(
    {
      page: query?.pageIndex,
      size: query?.pageSize,
      keyword: query?.keyword,
      sort: [`${query.sortBy?.key}:${query.sortBy?.order}`],
      status: query.columnCategoryFilter,
    },
    { refetchOnMountOrArgChange: true }
  );

  const columns: Array<TableColumnDefinition<TableData>> = useMemo(
    () => [
      {
        Header: t("table.header.name"),
        id: "name",
        accessor: "name",
        width: "250px",
      },
      {
        Header: t("table.header.userID"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            leftIcon={
              <FontIcon
                marginTop={4}
                customIcon={<FontAwesomeIcon size="lg" icon={faFingerprint} />}
              />
            }
          >
            {row?.original.id.toUpperCase().substr(0, 8)}
          </Badge>
        ),
        id: "id",
        accessor: "id",
        width: "250px",
      },
      {
        Header: t("table.header.email"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            leftIcon={
              <FontIcon
                marginTop={3}
                customIcon={<FontAwesomeIcon size="lg" icon={faEnvelope} />}
              />
            }
            status="break"
          >
            {row?.original.email}
          </Badge>
        ),
        id: "email",
        accessor: "email",
        width: "250px",
      },
      {
        Header: t("table.header.createdAt"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <span>{formatDate(row?.original.createdAt)}</span>
        ),
        id: "createdAt",
        accessor: "createdAt",
        width: "250px",
      },
      {
        Header: t("table.header.status"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          const isActive = row?.original.status === "us-active";
          const badgeStatusColor = isActive ? "success" : "danger";
          const status = isActive
            ? t("app.status.user.active")
            : t("app.status.user.deactivated");

          const icon = isActive ? (
            <FontIcon
              marginTop={3}
              customIcon={<FontAwesomeIcon size="lg" icon={faCheckCircle} />}
            />
          ) : (
            <FontIcon
              marginTop={3}
              customIcon={<FontAwesomeIcon size="lg" icon={faStopCircle} />}
            />
          );

          return (
            <Badge status={badgeStatusColor} variant="pill" leftIcon={icon}>
              {status}
            </Badge>
          );
        },
        id: "status",
        accessor: "status",
        width: "150px",
      },
    ],
    []
  );

  const memoizedData: Array<TableData> = useMemo(
    () =>
      currentData?.items?.map((user) => ({
        id: user?.id || "",
        name: user?.name || "",
        email: user?.email || "",
        createdAt: user?.createdAt || "",
        status: user?.status || "",
      })) || [],
    [currentData]
  );

  const onFetchData = ({
    pageIndex,
    pageSize,
    keyword: queryKeyword,
    sortBy,
    columnCategoryFilter,
  }: TableQuery) => {
    updateQuery({
      pageIndex,
      pageSize,
      keyword: queryKeyword || "",
      sortBy,
      columnCategoryFilter,
    });
  };

  if (isError) return <div>{t("app.page.error.api")}</div>;

  return (
    <div className="w-full min-w-full p-2">
      <div>
        <Table
          headerIcon={
            <FontIcon
              marginTop={4}
              customIcon={<FontAwesomeIcon icon={faList} />}
            />
          }
          headerText={t("card.header.list")}
          tableData={memoizedData}
          tableColumns={columns}
          showPagination
          showToolbar
          onRowSelect={(row) =>
            navigate(makeRoute(AllRoutes.USER_DETAIL, { id: row.original?.id }))
          }
          totalCountOfRecords={currentData?.count as number}
          query={{
            pageIndex: query.pageIndex,
            pageSize: query.pageSize,
            keyword: query.keyword,
            sortBy: query.sortBy,
          }}
          isLoading={isFetching}
          onFetchData={onFetchData}
          shouldAddEmptyRowsOnLastPage={currentData?.last}
          columnTypeFilter={[
            {
              label: t("app.status.user.new"),
              category: "us-new",
              icon: <FontAwesomeIcon icon={faInfoCircle} />,
            },
            {
              label: t("app.status.user.active"),
              category: "us-active",
              icon: <FontAwesomeIcon icon={faInfoCircle} />,
            },
            {
              label: t("app.status.user.pending"),
              category: "us-pending",
              icon: <FontAwesomeIcon icon={faInfoCircle} />,
            },
            {
              label: t("app.status.user.deleted"),
              category: "us-deleted",
              icon: <FontAwesomeIcon icon={faInfoCircle} />,
            },
            {
              label: t("app.status.user.deactivated"),
              category: "us-deactivated",
              icon: <FontAwesomeIcon icon={faInfoCircle} />,
            },
          ]}
        />
      </div>
    </div>
  );
}
